import React, { useState } from "react";
import styles from "./fbm-listings-search-item.module.css";
import ProductImage from "@/components/ProductImage/ProductImage";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Pill from "@/components/Pill/Pill";
import IconPlus from "@/components/icons/IconPlus";
import Button from "@/components/forms/Button/Button";
import { IconArrowDown, IconArrowUp } from "@/components/icons/IconArrow";
import { SkuGenerationMode } from "@boxem/core-api-schema";
import { useUserContext } from "@/contexts/UserContext";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import { productConditionToString, generateDefaultSku } from "@/app/fba/utils";
import { MockFormula } from "@/app/settings/variable_sku/variable_sku";
import Flex from "@/components/Flex/Flex";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import ListingStatusBadge from "@/components/ListingStatusBadge/ListingStatusBadge";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";

type FbmListingsSearchItemProps = {
  product: Product;
  onAddListing: (product: Partial<Product>, sellerItemIndex: number, autofillSKU: string, replenish: boolean) => void;
  setActiveTab: (tab: "form" | "details" | "offers") => void;
};

const FbmListingsSearchItem = ({
  product,
  onAddListing,
  setActiveTab,
}: FbmListingsSearchItemProps) => {
  const { seller } = useUserContext();
  const { allSellerSkus } = useInventoryContext();
  const [collapsed, setCollapsed] = useState<boolean>(true);

  const handleOpenFormTab = (sellerDataIndex: number = 0) => {
    const skuGenerationOption = (seller?.settings?.skuGenerationOption as SkuGenerationMode) ?? SkuGenerationMode.none;
    const formula = !!seller?.settings?.skuGenerationFormula ? JSON.parse(seller?.settings?.skuGenerationFormula) : MockFormula;
    const autoFillSku = generateDefaultSku(product, sellerDataIndex, skuGenerationOption, formula, allSellerSkus, seller.settings.skuGenerationCount);

    onAddListing({ ...product }, sellerDataIndex, autoFillSku, false);
    setActiveTab("form");
  };

  return (
    <div className={styles.listItem}>
      <div className={styles.searchItemContainer}>
        <ProductImage imageUrl={product.image} alt={product.name} />

        <Flex className={styles.minWidth0} direction="column" align="start" style={{width:"100%"}}>
          <Flex className={styles.listItemLeftColumn} direction="row" justify="space-between" align="stretch">
            <Tooltip title={product.name}>
              <a className={`strong ${styles.listItemTitle}`} href={`https://www.amazon.com/dp/${product.asin}`} target="_blank">
                {product.name}
              </a>
            </Tooltip>
          </Flex>

          <Flex className={styles.listItemLeftColumn} direction="column" align="start">
            <Flex justify="space-between" align="center" style={{width:"100%"}}>
              <Flex direction="column">
                <CopyToClipBoard value={product.asin} tooltipText="Copy ASIN">
                  <Flex align="center">
                    <p className="weak small-text">ASIN:</p>
                    &nbsp;
                    <p className="strong small-text">{product.asin}</p>
                  </Flex>
                </CopyToClipBoard>
                {!!product.salesRank && (
                  <Flex align="center">
                    <Flex align="center">
                      <p className="weak small-text">Category:</p>
                      &nbsp;
                      <a className="strong small-text" href={product.categoryLink} target="_blank">{product.category}</a>
                    </Flex>
                    <Flex align="center">
                      <p className="weak small-text">&nbsp;· Sales Rank:</p>
                      &nbsp;
                      <p className="strong small-text"><Pill>{product.salesRank.toLocaleString("en-US", { style: "decimal", maximumFractionDigits: 2 })}</Pill></p>
                    </Flex>
                  </Flex>
                )}
              </Flex>

              <Button
                type="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenFormTab(0);
                }}
              >
                <Flex gap={0.5} align="center" justify="center">
                  <IconPlus />
                  New SKU
                </Flex>
              </Button>
            </Flex>
          </Flex> 
        </Flex>
      </div>

      {(product.sellerData && product.sellerData.length > 0) && (
        <Flex direction="column" className={styles.listItemMultipleSkuContainer}>
          <Flex
            className={`${styles.multipleSkuColumn} ${collapsed ? styles.collapsed : ''}`}
            direction="column"
            gap={1}
          >
            {product.sellerData?.map((sellerData, index) => (
              <Flex key={index} justify="end" align="center">
                {(sellerData.status === "ERROR" || sellerData.status === "PROCESSING") && (
                  <div className={styles.listingBadgeContainer}>
                    <ListingStatusBadge
                      sellerData={sellerData}
                      proccessingTooltipPosition="top"
                    />
                  </div>
                )}
                <p className={`weak small-text ${styles.labelNoTruncate}`}>SKU:</p>
                &nbsp;
                <Tooltip
                  title={sellerData.sku}
                >
                  <p className={`strong small-text ${styles.truncate}`}>{sellerData.sku}</p>
                </Tooltip>
                {sellerData.condition && (
                  <>
                    <p className={`weak small-text ${styles.labelNoTruncate}`}>&nbsp;· Condition:</p>
                    &nbsp;
                    <p className={`strong small-text ${styles.labelNoTruncate}`}>{productConditionToString(sellerData.condition)}</p>
                  </>
                )}
                <StatusBadge label={`${sellerData?.quantity ?? 0} in stock`} style={{marginLeft:"1ch"}} />
                <Flex align="center" style={{marginLeft:"1ch"}}>
                  {sellerData.status === "ERROR" || sellerData.status === "PROCESSING" ? (
                    <Tooltip title={ sellerData.status === "ERROR" ? "It seems there was an issue verifying your listing. Click on the error badge to see details" : "You can't add this listing to your shipment until we've verified there were no issues"}>
                      <Button
                        type="primary"
                        disabled={true}
                      >
                        <IconPlus/>
                      </Button>
                    </Tooltip>
                  ) : (
                    <Button
                      type="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddListing(product, index, sellerData.sku, true);
                      }}
                    >
                      <IconPlus/>
                    </Button>
                  )}
                </Flex>
              </Flex>
            ))}
          </Flex>
          {product.sellerData?.length > 1 && (
              <Button
                type="text"
                onClick={() => setCollapsed(!collapsed)}
              >
                <Flex style={{width:"100%"}} align="center">
                  <div className={styles.divider}></div>

                  <Flex shrink={0} basis="max-content" align="center" gap={2} style={{whiteSpace:"nowrap"}}>
                    {collapsed ? (
                      <>
                        <label className="small-text strong">More SKUs ({product.sellerData.length - 1} more)</label>
                        <IconArrowDown />
                      </> 
                    ) : (
                      <>
                        <label className="small-text strong">Less SKUs</label>
                        <IconArrowUp />
                      </>
                    )}
                  </Flex>

                  <div className={styles.divider}></div>
                </Flex>
              </Button>
            )}
        </Flex>
      )}
    </div>
  );
};

export default FbmListingsSearchItem;